import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Calendar, MapPin } from "lucide-react";
import HTML_Renderer from "./HTML_Renderer";

interface PressReleaseProps {
  title: string;
  date: string;
  location: string;
  subheading: string;
  content: string;
  quotes: Array<{
    text: string;
    author: string;
    title?: string;
  }>;
  features?: string[];
  contact?: {
    email: string;
    phone?: string;
  };
  boilerplate?: string;
  isExpanded?: boolean;
  img: any;
  onToggle?: () => void;
}

export default function PressRelease({
  title,
  date,
  img,
  location,
  subheading,
  content,
  quotes,
  features,
  contact,
  boilerplate,
  isExpanded = false,
  onToggle,
}: PressReleaseProps) {
  const handleDownloadPDF = () => {
    // This would generate a PDF in a real implementation
    console.log("Generating PDF for:", title);
  };

  return (
    <Card className="bg-musicCharcoal border-musicGrey/20 hover:border-musicRed/30 transition-all duration-500">
      <CardContent className="p-8">
        <div className="flex justify-between items-start mb-6 flex-wrap">
          <div className="flex items-center space-x-4 text-musicGrey text-sm">
            <div className="flex items-center space-x-1">
              <Calendar size={16} />
              <span>{date}</span>
            </div>
            <div className="flex items-center space-x-1">
              <MapPin size={16} />
              <span>{location}</span>
            </div>
          </div>
          <a
            onClick={handleDownloadPDF}
            className="download-btn inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border bg-background hover:bg-accent hover:shadow-lg transition-all duration-300 h-9 rounded-md px-3 text-musicGrey border-musicGrey/30 hover:text-musicWhite hover:border-musicRed"
          >
            <Download size={16} className="mr-2" />
            Download PDF
          </a>
        </div>

        <h2
          className="text-2xl md:text-3xl font-bold text-musicWhite mb-4 cursor-pointer hover:text-musicRed transition-colors"
          onClick={onToggle}
        >
          {title}
        </h2>

        <h3 className="text-lg text-musicRed mb-6 font-medium">{subheading}</h3>

        {isExpanded && (
          <div className="space-y-6 animate-fade-in">
            {/*<img src={img} />*/}
            <div className="prose prose-invert max-w-none">
              <HTML_Renderer content={content} />

              {quotes.map((quote, index) => (
                <blockquote
                  key={index}
                  className="border-l-4 border-musicRed pl-6 py-4 my-6 rounded-r-md"
                >
                  <p className="text-musicWhite italic text-lg mb-2">
                    "{quote.qoute}"
                  </p>
                  <cite className="text-musicRed font-medium">
                    {quote.speaker}
                    {quote.jobTitle && (
                      <span className="text-musicGrey">, {quote.jobTitle}</span>
                    )}
                  </cite>
                </blockquote>
              ))}

              {features && (
                <div className="my-6">
                  <h4 className="text-musicWhite font-bold mb-3">
                    Key features:
                  </h4>
                  <ul className="space-y-2">
                    {features.map((feature, index) => (
                      <li
                        key={index}
                        className="text-musicGrey flex items-start"
                      >
                        <span className="text-musicRed mr-2">–</span>
                        <span dangerouslySetInnerHTML={{ __html: feature }} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {contact && (
                <div className=" p-6 rounded-md my-6">
                  <h4 className="text-musicWhite font-bold mb-3">
                    For partnership or media enquiries:
                  </h4>
                  <p className="text-musicGrey">
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-musicRed hover:underline"
                    >
                      {contact.email}
                    </a>
                    {" | "}
                    <a
                      href={`tel:${contact.phone}`}
                      className="text-musicRed hover:underline"
                    >
                      {contact.phone}
                    </a>
                  </p>
                </div>
              )}

              {boilerplate && (
                <div className="border-t border-musicGrey/20 pt-6 mt-8">
                  <p className="text-musicGrey text-sm italic">{boilerplate}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!isExpanded && (
          <Button
            variant="ghost"
            onClick={onToggle}
            className="text-musicRed hover:text-musicWhite mt-4"
          >
            Read more →
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
